import { useState, useEffect, useMemo, useCallback, ReactNode, MouseEvent } from 'react';
import {
	MagnifyingGlassIcon,
	XMarkIcon,
	CheckCircleIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	EyeIcon,
	PaperAirplaneIcon,
	CreditCardIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AppLayout from '../../components/layout/AppLayout';
import { Modal, Spinner, ConfirmDialog } from '../../components/ui';
import { invoiceApi } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { Invoice, InvoiceTask } from '../../types/invoice';

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatDate = (dateStr: string): string => {
	if (!dateStr) return '—';
	const d = new Date(dateStr);
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatSize = (size: string): string => {
	if (!size) return '';
	return size.charAt(0).toUpperCase() + size.slice(1).toLowerCase().replace(/_/g, ' ');
};

const formatHairLength = (length: string): string => {
	if (!length) return '';
	return length
		.split('_')
		.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ');
};

// ─── Types ──────────────────────────────────────────────────────

type InvoiceStatus = 'PAID' | 'PENDING' | 'OVERDUE';
type FilterTab = 'all' | 'paid' | 'pending' | 'overdue';

// ─── Status Styling ─────────────────────────────────────────────

const STATUS_STYLES: Record<
	InvoiceStatus,
	{ badge: string; border: string; icon: string; label: string }
> = {
	PAID: {
		badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
		border: 'border-l-emerald-500',
		icon: 'text-emerald-600',
		label: 'Paid',
	},
	PENDING: {
		badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
		border: 'border-l-amber-500',
		icon: 'text-amber-600',
		label: 'Pending',
	},
	OVERDUE: {
		badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
		border: 'border-l-red-500',
		icon: 'text-red-600',
		label: 'Overdue',
	},
};

const StatusIcon = ({ status, className }: { status: InvoiceStatus; className?: string }) => {
	const cls = className || 'w-4 h-4';
	switch (status) {
		case 'PAID':
			return <CheckCircleIcon className={cls} />;
		case 'OVERDUE':
			return <ExclamationTriangleIcon className={cls} />;
		default:
			return <ClockIcon className={cls} />;
	}
};

// ─── Stat Card ──────────────────────────────────────────────────

const StatCard = ({
	icon,
	value,
	label,
	iconBg,
}: {
	icon: ReactNode;
	value: number;
	label: string;
	iconBg: string;
}) => (
	<div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm flex items-center gap-3.5">
		<div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${iconBg}`}>
			{icon}
		</div>
		<div>
			<div className="text-2xl font-extrabold text-gray-900 dark:text-white">{value}</div>
			<div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
		</div>
	</div>
);

// ─── Main Component ─────────────────────────────────────────────

const Invoices = () => {
	const { selectedLocationId } = useProviderProfileStore();
	const [activeTab, setActiveTab] = useState<FilterTab>('all');
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, overdue: 0 });
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [resendInvoice, setResendInvoice] = useState<Invoice | null>(null);
	const [isResending, setIsResending] = useState(false);

	const fetchInvoices = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const response = await invoiceApi.getList(selectedLocationId || undefined);
			setInvoices(response.invoices);
			setStats({
				total: response.totalInvoices,
				paid: response.paidCount,
				pending: response.pendingCount,
				overdue: response.overdueCount,
			});
		} catch {
			setError('Failed to load invoices. Please try again.');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId]);

	useEffect(() => {
		fetchInvoices();
	}, [fetchInvoices]);

	// Filtered invoices
	const filteredInvoices = useMemo(() => {
		let list = invoices;

		// Tab filter
		if (activeTab !== 'all') {
			list = list.filter(i => i.status === activeTab.toUpperCase());
		}

		// Search filter
		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase();
			list = list.filter(
				i =>
					i.customerName.toLowerCase().includes(q) ||
					i.invoiceNumber.toLowerCase().includes(q) ||
					i.serviceName.toLowerCase().includes(q)
			);
		}

		return list;
	}, [invoices, activeTab, searchQuery]);

	const handleResend = useCallback((inv: Invoice, e?: MouseEvent) => {
		e?.stopPropagation();
		setResendInvoice(inv);
	}, []);

	const handleResendConfirm = useCallback(async () => {
		if (!resendInvoice) return;
		setIsResending(true);
		try {
			await invoiceApi.send({
				invoiceId: resendInvoice.id,
				discount: resendInvoice.discount,
				miscCharges: resendInvoice.miscCharges,
				miscDescription: resendInvoice.miscDescription || '',
				sendVia: 'email,sms',
			});
			toast.success(`Invoice resent to ${resendInvoice.customerName}`);
			setResendInvoice(null);
			fetchInvoices();
		} catch {
			toast.error('Failed to resend invoice. Please try again.');
		} finally {
			setIsResending(false);
		}
	}, [resendInvoice, fetchInvoices]);

	const handleView = useCallback((inv: Invoice, e?: MouseEvent) => {
		e?.stopPropagation();
		setSelectedInvoice(inv);
	}, []);

	const TABS: { key: FilterTab; label: string }[] = [
		{ key: 'all', label: 'All' },
		{ key: 'paid', label: 'Paid' },
		{ key: 'pending', label: 'Pending' },
		{ key: 'overdue', label: 'Overdue' },
	];

	if (isLoading) {
		return (
			<AppLayout>
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			</AppLayout>
		);
	}

	if (error) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<button
						onClick={() => {
							setIsLoading(true);
							fetchInvoices();
						}}
						className="px-4 py-2 rounded-lg font-semibold text-sm text-white"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						Retry
					</button>
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoices</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Track all invoices sent to customers
				</p>
			</div>

			{/* Stats Row */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
				<StatCard
					icon={
						<CreditCardIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
					}
					iconBg="bg-indigo-100 dark:bg-indigo-900/30"
					value={stats.total}
					label="Total Invoices"
				/>
				<StatCard
					icon={
						<CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
					}
					iconBg="bg-emerald-100 dark:bg-emerald-900/30"
					value={stats.paid}
					label="Paid"
				/>
				<StatCard
					icon={<ClockIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
					iconBg="bg-amber-100 dark:bg-amber-900/30"
					value={stats.pending}
					label="Pending"
				/>
				<StatCard
					icon={
						<ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
					}
					iconBg="bg-red-100 dark:bg-red-900/30"
					value={stats.overdue}
					label="Overdue"
				/>
			</div>

			{/* Filter Bar */}
			<div className="flex flex-col sm:flex-row gap-3 mb-5">
				{/* Tabs */}
				<div className="flex bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
					{TABS.map(tab => (
						<button
							key={tab.key}
							onClick={() => setActiveTab(tab.key)}
							className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
								activeTab === tab.key
									? 'text-white'
									: 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400'
							}`}
							style={
								activeTab === tab.key
									? {
											background: 'linear-gradient(135deg, #667eea, #764ba2)',
										}
									: undefined
							}
						>
							{tab.label}
						</button>
					))}
				</div>

				{/* Search */}
				<div className="relative flex-1 min-w-[200px]">
					<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
					<input
						type="text"
						placeholder="Search by client name or invoice number..."
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
					/>
					{searchQuery && (
						<button
							onClick={() => setSearchQuery('')}
							className="absolute right-3 top-1/2 -translate-y-1/2"
						>
							<XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
						</button>
					)}
				</div>
			</div>

			{/* Desktop Table */}
			<div className="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
				<table className="w-full">
					<thead>
						<tr className="bg-gray-50 dark:bg-gray-700/50">
							<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
								Client
							</th>
							<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
								Balance Due
							</th>
							<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
								You Receive
							</th>
							<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
								Status
							</th>
							<th className="px-5 py-3.5 w-16" />
						</tr>
					</thead>
					<tbody>
						{filteredInvoices.map(inv => {
							const style =
								STATUS_STYLES[inv.status as InvoiceStatus] || STATUS_STYLES.PENDING;
							return (
								<tr
									key={inv.id}
									onClick={() => setSelectedInvoice(inv)}
									className="border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
								>
									{/* Client */}
									<td className="px-5 py-4">
										<div className="flex items-center gap-3">
											<div
												className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
												style={{
													background:
														'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
												}}
											>
												{inv.customerInitials}
											</div>
											<div className="min-w-0">
												<p className="font-semibold text-gray-900 dark:text-white text-sm">
													{inv.customerName}
												</p>
												<p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
													{inv.serviceName}
												</p>
												<p className="text-xs text-gray-400 dark:text-gray-500">
													{formatDate(inv.appointmentDate)}
													{inv.status === 'OVERDUE' &&
														inv.daysAgo > 0 && (
															<span className="text-red-500 ml-1">
																&middot; {inv.daysAgo} days ago
															</span>
														)}
												</p>
											</div>
										</div>
									</td>

									{/* Customer Paid */}
									<td className="px-5 py-4">
										<p className="font-bold text-gray-900 dark:text-white text-sm">
											{formatCurrency(inv.balanceDue)}
										</p>
										<p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
											{inv.tipAmount > 0
												? `incl. tip ${formatCurrency(inv.tipAmount)}`
												: 'no tip'}
										</p>
									</td>

									{/* You Receive */}
									<td className="px-5 py-4">
										<p
											className={`font-bold text-sm ${
												inv.status === 'PAID'
													? 'text-emerald-600 dark:text-emerald-400'
													: 'text-gray-400 dark:text-gray-500'
											}`}
										>
											{formatCurrency(inv.youReceive)}
										</p>
										<p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
											fee {formatCurrency(inv.stripeFee ?? 0)}
											{inv.status !== 'PAID' && ' \u00B7 on payment'}
										</p>
									</td>

									{/* Status */}
									<td className="px-5 py-4">
										<span
											className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${style.badge}`}
										>
											<StatusIcon
												status={inv.status as InvoiceStatus}
												className={`w-3.5 h-3.5 ${style.icon}`}
											/>
											{style.label}
										</span>
										{inv.paymentMethod && (
											<p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
												{inv.paymentMethod}
												{inv.paidAt
													? ` \u00B7 ${formatDate(inv.paidAt)}`
													: ''}
											</p>
										)}
									</td>

									{/* Action */}
									<td className="px-5 py-4">
										{inv.status === 'PAID' ? (
											<button
												onClick={e => handleView(inv, e)}
												className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
											>
												<EyeIcon className="w-4 h-4" />
											</button>
										) : (
											<button
												onClick={e => handleResend(inv, e)}
												className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
											>
												<PaperAirplaneIcon className="w-4 h-4" />
											</button>
										)}
									</td>
								</tr>
							);
						})}
						{filteredInvoices.length === 0 && (
							<tr>
								<td
									colSpan={5}
									className="px-5 py-12 text-center text-gray-400 dark:text-gray-500"
								>
									No invoices found
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{/* Mobile Cards */}
			<div className="md:hidden flex flex-col gap-3">
				{filteredInvoices.map(inv => {
					const style =
						STATUS_STYLES[inv.status as InvoiceStatus] || STATUS_STYLES.PENDING;
					return (
						<div
							key={inv.id}
							onClick={() => setSelectedInvoice(inv)}
							className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden cursor-pointer border-l-4 ${style.border} hover:shadow-md transition-all`}
						>
							{/* Card Header */}
							<div className="flex items-center gap-3 p-4">
								<div
									className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
									style={{
										background:
											'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
									}}
								>
									{inv.customerInitials}
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex justify-between items-start">
										<p className="font-bold text-gray-900 dark:text-white text-sm truncate">
											{inv.customerName}
										</p>
										<p
											className={`font-bold text-sm shrink-0 ml-2 ${
												inv.status === 'PAID'
													? 'text-emerald-600 dark:text-emerald-400'
													: 'text-gray-400 dark:text-gray-500'
											}`}
										>
											{formatCurrency(inv.youReceive)}
										</p>
									</div>
									<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
										{inv.serviceName}
									</p>
									<p className="text-xs text-gray-400 dark:text-gray-500">
										{formatDate(inv.appointmentDate)}
										{inv.status === 'OVERDUE' && inv.daysAgo > 0 && (
											<span className="text-red-500 ml-1">
												&middot; {inv.daysAgo} days ago
											</span>
										)}
									</p>
								</div>
							</div>

							{/* Card Footer */}
							<div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-100 dark:border-gray-700">
								<span
									className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${style.badge}`}
								>
									<StatusIcon
										status={inv.status as InvoiceStatus}
										className={`w-3.5 h-3.5 ${style.icon}`}
									/>
									{style.label}
								</span>
								{inv.status === 'PAID' ? (
									<button
										onClick={e => handleView(inv, e)}
										className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
									>
										<EyeIcon className="w-4 h-4" />
									</button>
								) : (
									<button
										onClick={e => handleResend(inv, e)}
										className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
									>
										<PaperAirplaneIcon className="w-4 h-4" />
									</button>
								)}
							</div>
						</div>
					);
				})}
				{filteredInvoices.length === 0 && (
					<div className="text-center py-12 text-gray-400 dark:text-gray-500">
						No invoices found
					</div>
				)}
			</div>

			{/* Invoice Detail Modal */}
			<Modal
				isOpen={!!selectedInvoice}
				onClose={() => setSelectedInvoice(null)}
				title={`${selectedInvoice?.invoiceNumber || ''} — ${selectedInvoice?.customerName || ''}`}
				size="lg"
			>
				{selectedInvoice && (
					<InvoiceDetailContent
						invoice={selectedInvoice}
						onClose={() => setSelectedInvoice(null)}
						onResend={handleResend}
					/>
				)}
			</Modal>

			{/* Resend Confirmation Dialog */}
			<ConfirmDialog
				isOpen={!!resendInvoice}
				onClose={() => setResendInvoice(null)}
				onConfirm={handleResendConfirm}
				title="Resend Invoice"
				message={`Are you sure you want to resend the invoice to ${resendInvoice?.customerName || ''}?`}
				confirmLabel="Yes, Resend"
				cancelLabel="No"
				variant="primary"
				isLoading={isResending}
				icon={
					<PaperAirplaneIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
				}
			/>
		</AppLayout>
	);
};

// ─── Invoice Detail Modal Content ───────────────────────────────

const InvoiceDetailContent = ({
	invoice,
	onClose,
	onResend,
}: {
	invoice: Invoice;
	onClose: () => void;
	onResend: (inv: Invoice) => void;
}) => {
	const style = STATUS_STYLES[invoice.status as InvoiceStatus] || STATUS_STYLES.PENDING;
	const serviceDetails = [formatSize(invoice.size), formatHairLength(invoice.hairLength)]
		.filter(Boolean)
		.join(', ');

	return (
		<div>
			{/* Client Information */}
			<div className="mb-6">
				<h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
					Client Information
				</h4>
				<DetailRow label="Client Name" value={invoice.customerName} />
				<DetailRow label="Service" value={invoice.serviceName} />
				<DetailRow label="Appointment Date" value={formatDate(invoice.appointmentDate)} />
				{serviceDetails && <DetailRow label="Details" value={serviceDetails} />}
			</div>

			{/* Service Breakdown */}
			<div className="mb-6">
				<h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
					Service Breakdown
				</h4>
				<div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
					{/* Tasks */}
					{invoice.tasks.length > 0 ? (
						invoice.tasks.map((task: InvoiceTask, idx: number) => (
							<div
								key={idx}
								className={`flex justify-between py-2.5 ${idx < invoice.tasks.length - 1 ? 'border-b border-gray-200 dark:border-gray-600' : ''}`}
							>
								<div>
									<p className="font-semibold text-gray-900 dark:text-white text-sm">
										{task.description || task.taskType}
									</p>
									{task.durationMinutes > 0 && (
										<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
											{task.durationMinutes} min
										</p>
									)}
								</div>
								<p className="font-bold text-gray-900 dark:text-white text-sm">
									{formatCurrency(task.price)}
								</p>
							</div>
						))
					) : (
						<div className="flex justify-between py-2.5">
							<div>
								<p className="font-semibold text-gray-900 dark:text-white text-sm">
									{invoice.serviceName}
								</p>
								{serviceDetails && (
									<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
										{serviceDetails}
									</p>
								)}
							</div>
							<p className="font-bold text-gray-900 dark:text-white text-sm">
								{formatCurrency(invoice.servicePrice)}
							</p>
						</div>
					)}

					{/* Deposit */}
					{invoice.deposit > 0 && (
						<div className="flex justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
							<p className="text-sm text-gray-500 dark:text-gray-400">Deposit</p>
							<p className="font-bold text-gray-900 dark:text-white text-sm">
								-{formatCurrency(invoice.deposit)}
							</p>
						</div>
					)}

					{/* Discount */}
					{invoice.discount > 0 && (
						<div className="flex justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
							<p className="text-sm text-gray-500 dark:text-gray-400">Discount</p>
							<p className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">
								-{formatCurrency(invoice.discount)}
							</p>
						</div>
					)}

					{/* Misc Charges */}
					{invoice.miscCharges > 0 && (
						<div className="flex justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									Misc Charges
								</p>
								{invoice.miscDescription && (
									<p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
										{invoice.miscDescription}
									</p>
								)}
							</div>
							<p className="font-bold text-gray-900 dark:text-white text-sm">
								{formatCurrency(invoice.miscCharges)}
							</p>
						</div>
					)}

					{/* Stripe Fee */}
					<div className="flex justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
						<div>
							<p className="font-semibold text-gray-900 dark:text-white text-sm">
								Processing Fee
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
								BBP Service Charge
							</p>
						</div>
						<p className="font-bold text-gray-900 dark:text-white text-sm">
							{formatCurrency(invoice.stripeFee ?? 0)}
						</p>
					</div>

					{/* Tip */}
					<div className="flex justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
						<div>
							<p className="font-semibold text-gray-900 dark:text-white text-sm">
								Customer Tip
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
								Added by customer
							</p>
						</div>
						<p className="font-bold text-gray-900 dark:text-white text-sm">
							{formatCurrency(invoice.tipAmount)}
						</p>
					</div>
				</div>
			</div>

			{/* Payment Summary */}
			<div className="mb-6">
				<h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
					Payment Summary
				</h4>
				<DetailRow
					label="Customer Paid"
					value={formatCurrency(invoice.customerPaid)}
					bold
				/>
				<DetailRow
					label="Processing Fee"
					value={`-${formatCurrency(invoice.stripeFee ?? 0)}`}
					valueColor="text-red-500"
				/>
				{(invoice.transactionFee ?? 0) > 0 && (
					<DetailRow
						label="Transaction Fee"
						value={`-${formatCurrency(invoice.transactionFee ?? 0)}`}
						valueColor="text-red-500"
					/>
				)}
				<div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg mt-3">
					<span className="font-bold text-gray-900 dark:text-white">You Receive</span>
					<span className="text-xl font-extrabold text-indigo-600 dark:text-indigo-400">
						{formatCurrency(invoice.youReceive)}
					</span>
				</div>
			</div>

			{/* Payment Status */}
			<div className="mb-6">
				<h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
					Payment Status
				</h4>
				<DetailRow
					label="Status"
					value={
						<span
							className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${style.badge}`}
						>
							<StatusIcon
								status={invoice.status as InvoiceStatus}
								className={`w-3.5 h-3.5 ${style.icon}`}
							/>
							{style.label}
						</span>
					}
				/>
				{invoice.paymentMethod && (
					<DetailRow label="Payment Method" value={invoice.paymentMethod} />
				)}
				{invoice.paidAt && (
					<DetailRow label="Payment Date" value={formatDate(invoice.paidAt)} />
				)}
				{invoice.sentAt && (
					<DetailRow label="Sent Date" value={formatDate(invoice.sentAt)} />
				)}
				{invoice.balanceDue > 0 && (
					<DetailRow
						label="Balance Due"
						value={formatCurrency(invoice.balanceDue)}
						valueColor="text-red-500"
						bold
					/>
				)}
			</div>

			{/* Actions */}
			<div className="flex gap-3 pt-5 border-t border-gray-200 dark:border-gray-700">
				<button
					onClick={onClose}
					className="flex-1 py-3 rounded-lg font-semibold text-sm text-indigo-600 dark:text-indigo-400 border-2 border-indigo-600 dark:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
				>
					Close
				</button>
				{invoice.status !== 'PAID' && (
					<button
						onClick={() => {
							onClose();
							onResend(invoice);
						}}
						className="flex-1 py-3 rounded-lg font-semibold text-sm text-white transition-all hover:shadow-lg"
						style={{
							background: 'linear-gradient(135deg, #667eea, #764ba2)',
						}}
					>
						Resend Invoice
					</button>
				)}
			</div>
		</div>
	);
};

// ─── Detail Row ─────────────────────────────────────────────────

const DetailRow = ({
	label,
	value,
	bold,
	valueColor,
}: {
	label: string;
	value: ReactNode;
	bold?: boolean;
	valueColor?: string;
}) => (
	<div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
		<span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
		<span
			className={`text-sm text-right ${bold ? 'font-bold' : 'font-semibold'} ${valueColor || 'text-gray-900 dark:text-white'}`}
		>
			{value}
		</span>
	</div>
);

export default Invoices;
